import { FC, useState } from 'react'

import { isImage as checkIsImage } from '../../../../utils'
import {
  FilePreviewProps,
  FilePreviewType,
  FileViewMode,
  UploadAreaType,
} from '../../UploadArea.types'
import { minImageWidthInPx } from '../../UploadArea.utils'
import { FilePreviewActions } from '../FilePreviewActions'
import { ImagePreviewModal } from '../ImagePreviewModal'
import { ProgressBar } from '../ProgressBar'
import * as S from './FilePreview.styles'
import { Preview } from './Preview'

export const FilePreview: FC<FilePreviewProps> = ({
  allFiles,
  file,
  filePreviewActions,
  fileUploadProgress,
  filesWithError,
  index,
  isFileSelected,
  isFileEnabled,
  filePreviewType,
  isSelecting,
  onMakeAsCover,
  onRetryUpload,
  onSelectFile,
  providedDraggable,
  uploadAreaType,
  removeFile,
  fileViewMode = FileViewMode.grid,
  onToggleEnableAsset,
  fileCustomInfo,
  enableZoom,
  enableImageSize,
}) => {
  const [openImagePreview, setOpenImagePreview] = useState(false)

  const decodedFileName = decodeURIComponent(file?.fileName || '')
  const extension = (((file.fileName || file.url) ?? '').split('.').pop() ?? '').toUpperCase()
  const fileIndex = allFiles.indexOf(file)
  const filePosition = fileIndex + 1
  const isImageCover = fileIndex === 0
  const isDocument = uploadAreaType === UploadAreaType.document
  const isFileImage = checkIsImage(extension)

  const isPhotoGallery = filePreviewType === FilePreviewType.photoGallery
  const isLowResolution = !!(file.imageDetails && file.imageDetails.width < minImageWidthInPx)
  const isPhotoGalleryAndLowResolution = isPhotoGallery && isLowResolution

  const fileCurrentId = isDocument ? file.documentId : file.id
  const fileUploaded = !!file.s3Key

  /**
   * We pass files with errors with the original File object
   * in order to be able to retry to upload the file
   */
  const originalFile = filesWithError && fileCurrentId && filesWithError[fileCurrentId]
  const hasError = !!originalFile

  const isFileUploadedWithoutErrors = fileUploaded && !hasError

  const Wrapper = isDocument ? S.DocumentPreviewContainer : S.FilePreviewContainer

  const removeFileCb = removeFile ? () => removeFile(index) : undefined

  const handleOnRetryUpload = () => {
    if (onRetryUpload && originalFile) {
      onRetryUpload(originalFile)
    }
  }

  const handleSelectFile = () => {
    if (onSelectFile && fileCurrentId) {
      onSelectFile(fileCurrentId)
    }
  }

  return (
    <S.Container $viewMode={fileViewMode} $filePreviewType={filePreviewType}>
      <Wrapper
        data-testid="upload_item"
        ref={providedDraggable?.innerRef}
        {...providedDraggable?.draggableProps}
        {...providedDraggable?.dragHandleProps}
        $filePreviewType={filePreviewType}
        $viewMode={fileViewMode}
      >
        <Preview
          file={file}
          filePreviewType={filePreviewType}
          isImageCover={isImageCover}
          uploadAreaType={uploadAreaType}
          filesWithError={filesWithError}
          isFileEnabled={isFileEnabled}
          onMakeAsCover={onMakeAsCover}
          hasLowResolution={isPhotoGalleryAndLowResolution}
          onOpenImagePreview={setOpenImagePreview}
        />

        {isFileImage && file.imageDetails && enableImageSize ? (
          <S.FileSize>
            {file.imageDetails.height} x {file.imageDetails.width} px
          </S.FileSize>
        ) : null}

        {hasError ? <S.Error>Upload failed</S.Error> : null}

        {!!fileUploadProgress && !hasError ? (
          <ProgressBar progressPercentage={fileUploadProgress} />
        ) : null}

        {isFileImage && isFileUploadedWithoutErrors && openImagePreview ? (
          <ImagePreviewModal
            allFiles={allFiles}
            imageIndex={index}
            enableZoom={enableZoom}
            onCloseImagePreview={() => setOpenImagePreview(false)}
          />
        ) : null}
      </Wrapper>

      <FilePreviewActions
        assetId={file?.id}
        index={fileIndex}
        isImageCover={isImageCover}
        isFileSelected={isFileSelected}
        isSelecting={isSelecting}
        isFileUploaded={fileUploaded}
        isFileImage={isFileImage}
        isLowResolution={isPhotoGalleryAndLowResolution}
        resolution={file.imageDetails}
        hasError={hasError}
        onRetryUpload={handleOnRetryUpload}
        onSelectFile={handleSelectFile}
        isPhotoEnable={isFileEnabled}
        onToggleEnableAsset={onToggleEnableAsset ? () => onToggleEnableAsset(file) : undefined}
        filePosition={filePosition}
        onRemoveFile={removeFileCb}
        decodedFileName={decodedFileName}
        filePreviewType={filePreviewType}
        filePreviewActions={filePreviewActions}
        fileViewMode={fileViewMode}
        fileCustomInfo={fileCustomInfo}
        fileUrl={file?.url}
      />
    </S.Container>
  )
}
